import { useState, useMemo } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";


const GROUP_ICONS = {
  produce: (
    <svg width="17" height="17" viewBox="0 0 216 216" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M199.926 16L159.526 56.4" stroke="#2A952F" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M19.7359 207C18.2243 207.798 16.4967 208.091 14.8064 207.836C13.1161 207.581 11.5519 206.792 10.3432 205.583C9.13439 204.374 8.34461 202.81 8.08955 201.12C7.8345 199.429 8.1276 197.702 8.92588 196.19C8.92588 196.19 39.9259 96 80.3259 56.4001C90.8285 45.8975 105.073 39.9972 119.926 39.9972C134.779 39.9972 149.023 45.8975 159.526 56.4001C170.028 66.9026 175.929 81.1472 175.929 96C175.929 110.853 170.028 125.097 159.526 135.6C119.926 176 19.7359 207 19.7359 207Z" stroke="#E2A619" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M207.926 56H159.116" stroke="#2A952F" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M159.926 56.81V8" stroke="#2A952F" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M87.9259 136L117.626 165.7" stroke="#E2A619" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M80.3259 56.4L119.926 96" stroke="#E2A619" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
  ),
  meat: (
    <svg width="18" height="18" viewBox="0 0 264 157" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M191.425 53.2837C195.916 57.7747 195.916 65.0561 191.425 69.5472C186.933 74.0382 179.652 74.0382 175.161 69.5472C170.67 65.0561 170.67 57.7747 175.161 53.2837C179.652 48.7927 186.933 48.7927 191.425 53.2837Z" fill="#342B2B" stroke="#8BBAE6"/>
<path d="M8.02223 38.6962C149.352 259.405 254.923 80.2952 255.418 78.3861C254.916 76.4698 149.352 -102.633 8.00101 118.055" stroke="#8BBAE6" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M162.504 147.895C145.352 128.805 135.864 104.049 135.864 78.386C135.864 52.7233 145.352 27.9666 162.504 8.87744" stroke="#8BBAE6" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
  ),
  dairy: (
    <svg width="17" height="17" viewBox="0 0 224 168" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8.00211 128H15.4621C28.9121 128 40.2521 117 40.0021 103.54C39.8816 97.2549 37.3 91.2679 32.8121 86.866C28.3242 82.4642 22.2884 79.9988 16.0021 80H8.00211V56H216.002V152C216.002 154.122 215.159 156.157 213.659 157.657C212.159 159.157 210.124 160 208.002 160H8.00211V128Z" stroke="#E8DC3B" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M8.00211 56L168.002 8C180.732 8 192.941 13.0571 201.943 22.0589C210.945 31.0606 216.002 43.2696 216.002 56" stroke="#E8DC3B" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M64.0021 160V152C64.0021 143.513 67.3735 135.374 73.3747 129.373C79.3759 123.371 87.5152 120 96.0021 120C104.489 120 112.628 123.371 118.63 129.373C124.631 135.374 128.002 143.513 128.002 152V160" stroke="#E8DC3B" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M179.722 56C182.528 60.8648 184.004 66.3824 184.002 71.9983C184 77.6143 182.52 83.1308 179.711 87.9938C176.902 92.8567 172.863 96.8947 167.999 99.7021C163.135 102.51 157.618 103.988 152.002 103.988C146.386 103.988 140.869 102.51 136.005 99.7021C131.141 96.8947 127.102 92.8567 124.293 87.9938C121.484 83.1308 120.004 77.6143 120.002 71.9983C120 66.3824 121.476 60.8648 124.282 56" stroke="#E8DC3B" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

  ),
  bakery: (
    <svg width="17" height="17" viewBox="0 0 232 176" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M40 72C31.5131 72 23.3737 68.6286 17.3726 62.6274C11.3714 56.6263 8 48.4869 8 40C8 31.5131 11.3714 23.3737 17.3726 17.3726C23.3737 11.3714 31.5131 8 40 8H136C144.487 8 152.626 11.3714 158.627 17.3726C164.629 23.3737 168 31.5131 168 40C168 48.4869 164.629 56.6263 158.627 62.6274C152.626 68.6286 144.487 72 136 72" stroke="#69514F" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M120 8H192C200.487 8 208.626 11.3714 214.627 17.3726C220.629 23.3737 224 31.5131 224 40C224 48.4869 220.629 56.6263 214.627 62.6274C208.626 68.6286 200.487 72 192 72" stroke="#69514F" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M144 71V160C144 162.122 143.157 164.157 141.657 165.657C140.157 167.157 138.122 168 136 168H40C37.8783 168 35.8434 167.157 34.3431 165.657C32.8429 164.157 32 162.122 32 160V71" stroke="#69514F" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M200 71V160C200 162.122 199.157 164.157 197.657 165.657C196.157 167.157 194.122 168 192 168H136" stroke="#69514F" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
  ),
  cupboard: (
    <svg width="17" height="17" viewBox="0 0 176 224" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M136 40H40C22.3269 40 8 54.3269 8 72V184C8 201.673 22.3269 216 40 216H136C153.673 216 168 201.673 168 184V72C168 54.3269 153.673 40 136 40Z" stroke="#F65959" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M40 40V16C40 13.8783 40.8429 11.8434 42.3431 10.3431C43.8434 8.84285 45.8783 8 48 8H128C130.122 8 132.157 8.84285 133.657 10.3431C135.157 11.8434 136 13.8783 136 16V40" stroke="#F65959" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M72 40V8" stroke="#F65959" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M104 40V8" stroke="#F65959" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
  ),
  frozen: (
    <svg width="17" height="17" viewBox="0 0 233 282" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M226.066 81.7031L202.629 89.3086L217.09 109.23C217.854 110.223 218.411 111.36 218.729 112.572C219.047 113.784 219.118 115.047 218.939 116.287C218.76 117.527 218.335 118.719 217.687 119.792C217.04 120.865 216.184 121.797 215.17 122.533C214.157 123.27 213.006 123.795 211.785 124.079C210.565 124.363 209.3 124.4 208.065 124.187C206.83 123.973 205.651 123.515 204.596 122.838C203.542 122.161 202.634 121.28 201.926 120.246L187.5 100.324L173.039 120.246C171.55 122.181 169.365 123.458 166.949 123.807C164.532 124.156 162.075 123.548 160.1 122.113C158.124 120.678 156.787 118.529 156.371 116.124C155.956 113.718 156.495 111.245 157.875 109.23L172.336 89.3086L148.898 81.7031C146.533 80.9354 144.57 79.2596 143.44 77.0444C142.311 74.8291 142.107 72.2558 142.875 69.8906C143.643 67.5254 145.318 65.5621 147.534 64.4325C149.749 63.3028 152.322 63.0995 154.688 63.8672L178.125 71.4844V46.875C178.125 44.3886 179.113 42.004 180.871 40.2459C182.629 38.4877 185.014 37.5 187.5 37.5C189.986 37.5 192.371 38.4877 194.129 40.2459C195.887 42.004 196.875 44.3886 196.875 46.875V71.4844L220.312 63.8789C222.678 63.1097 225.252 63.3115 227.468 64.44C229.684 65.5685 231.362 67.5313 232.131 69.8965C232.9 72.2617 232.698 74.8356 231.57 77.0519C230.441 79.2683 228.478 80.9456 226.113 81.7148L226.066 81.7031ZM112.5 206.25C112.496 213.263 110.526 220.134 106.814 226.084C103.101 232.034 97.7943 236.823 91.4964 239.908C85.1984 242.993 78.1616 244.25 71.185 243.537C64.2084 242.823 57.5717 240.168 52.0286 235.872C46.4854 231.576 42.258 225.811 39.8264 219.234C37.3947 212.656 36.8564 205.528 38.2724 198.659C39.6884 191.791 43.0021 185.457 47.8372 180.377C52.6722 175.297 58.8347 171.675 65.625 169.922V131.25C65.625 128.764 66.6127 126.379 68.3709 124.621C70.129 122.863 72.5136 121.875 75 121.875C77.4864 121.875 79.871 122.863 81.6291 124.621C83.3873 126.379 84.375 128.764 84.375 131.25V169.922C92.4218 172.012 99.548 176.712 104.637 183.286C109.727 189.86 112.492 197.936 112.5 206.25ZM93.75 206.25C93.75 202.542 92.6503 198.916 90.5901 195.833C88.5298 192.75 85.6014 190.346 82.1753 188.927C78.7492 187.508 74.9792 187.137 71.3421 187.86C67.7049 188.584 64.364 190.37 61.7418 192.992C59.1195 195.614 57.3338 198.955 56.6103 202.592C55.8868 206.229 56.2581 209.999 57.6773 213.425C59.0964 216.851 61.4996 219.78 64.5831 221.84C67.6665 223.9 71.2916 225 75 225C79.9728 225 84.742 223.025 88.2583 219.508C91.7746 215.992 93.75 211.223 93.75 206.25ZM150 206.25C149.992 218.932 146.769 231.405 140.631 242.502C134.493 253.6 125.641 262.96 114.903 269.707C104.165 276.454 91.8919 280.368 79.2303 281.084C66.5687 281.799 53.9325 279.292 42.5029 273.797C31.0734 268.302 21.2239 259.999 13.8753 249.663C6.52658 239.327 1.91875 227.297 0.482532 214.697C-0.953682 202.097 0.82863 189.338 5.66284 177.614C10.497 165.889 18.2253 155.582 28.125 147.656V46.875C28.125 34.443 33.0636 22.5201 41.8544 13.7294C50.6451 4.9386 62.568 0 75 0C87.432 0 99.3549 4.9386 108.146 13.7294C116.936 22.5201 121.875 34.443 121.875 46.875V147.656C130.652 154.693 137.737 163.611 142.605 173.753C147.473 183.894 150 195 150 206.25ZM131.25 206.25C131.243 197.181 129.046 188.248 124.848 180.21C120.649 172.171 114.572 165.265 107.133 160.078C105.889 159.209 104.874 158.052 104.176 156.705C103.478 155.358 103.117 153.861 103.125 152.344V46.875C103.125 39.4158 100.162 32.2621 94.8874 26.9876C89.6129 21.7132 82.4592 18.75 75 18.75C67.5408 18.75 60.3871 21.7132 55.1126 26.9876C49.8382 32.2621 46.875 39.4158 46.875 46.875V152.344C46.8752 153.853 46.5109 155.34 45.8131 156.679C45.1153 158.017 44.1047 159.167 42.8672 160.031C34.9298 165.556 28.5507 173.033 24.3458 181.741C20.1409 190.45 18.2522 200.096 18.862 209.747C19.4718 219.398 22.5595 228.729 27.8271 236.839C33.0947 244.949 40.3642 251.564 48.9337 256.046C57.5033 260.527 67.0835 262.723 76.7494 262.423C86.4153 262.122 95.8404 259.334 104.115 254.329C112.389 249.323 119.233 242.269 123.987 233.847C128.74 225.426 131.241 215.921 131.25 206.25Z" fill="#06ACFF"/>
</svg>

  ),
  other: (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
    </svg>
  ),
};

const GROUP_LABELS = {
  produce: "Fresh Produce", meat: "Meat & Fish", dairy: "Dairy & Eggs",
  bakery: "Bakery", cupboard: "Cupboard", frozen: "Frozen", other: "Other",
};

// ── Sortable item row ──────────────────────────────────────────────────────────
function SortableItemRow({ id, item, display, isChecked, isManual, inPantry, isEditing, isMobile, appTheme, ACCENT, onToggle, onDelete, onPantryToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: "flex", alignItems: "center", gap: "10px",
        padding: "10px 12px", borderRadius: "14px",
        background: isChecked ? appTheme.softBackground : appTheme.cardBackground,
        border: appTheme.cardBorder,
        cursor: isEditing ? "default" : "pointer",
        transition: isDragging ? "none" : "all 0.18s ease",
      }}
      onClick={() => !isEditing && onToggle(item)}
    >
      {/* Drag handle — edit mode only */}
      {isEditing && (
        <span
          {...listeners}
          {...attributes}
          style={{ color: appTheme.mutedText, cursor: "grab", fontSize: "16px", flexShrink: 0, touchAction: "none", lineHeight: 1, paddingRight: "2px" }}
        >
          ⠿
        </span>
      )}

      {/* Checkbox — view mode only */}
      {!isEditing && (
        <div style={{ width: "22px", height: "22px", borderRadius: "50%", border: isChecked ? `2px solid ${ACCENT}` : "2px solid rgba(0,0,0,0.18)", background: isChecked ? ACCENT : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.18s ease" }}>
          {isChecked && <svg viewBox="0 0 12 12" width="11" height="11" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6l3 3 5-5"/></svg>}
        </div>
      )}

      <span style={{ flex: 1, fontSize: isMobile ? "15px" : "14px", color: isChecked && !isEditing ? appTheme.secondaryText : appTheme.primaryText, textDecoration: isChecked && !isEditing ? "line-through" : "none", opacity: isChecked && !isEditing ? 0.6 : 1, transition: "all 0.18s ease" }}>
        {display}
      </span>

      {/* Edit mode actions */}
      {isEditing && (
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <button
            onClick={(e) => { e.stopPropagation(); onPantryToggle(item); }}
            title={inPantry ? "Already in Kitchen Staples" : "Move to Kitchen Staples"}
            style={{ width: "30px", height: "30px", borderRadius: "999px", border: inPantry ? appTheme.accentBorder : appTheme.pillBorder, background: inPantry ? appTheme.accentLight : appTheme.pillBackground, color: inPantry ? ACCENT : appTheme.secondaryText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill={inPantry ? ACCENT : "none"} stroke={inPantry ? ACCENT : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(item, isManual); }}
            style={{ width: "30px", height: "30px", borderRadius: "999px", border: "1px solid rgba(220,38,38,0.25)", background: "rgba(220,38,38,0.06)", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "16px" }}
          >×</button>
        </div>
      )}
    </div>
  );
}

// ── Ghost card shown under the pointer while dragging ─────────────────────────
function DragGhostCard({ item, appTheme, isMobile }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "14px", background: appTheme.cardBackground, border: appTheme.accentBorder, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", opacity: 0.9 }}>
      <span style={{ color: appTheme.mutedText, fontSize: "16px", lineHeight: 1 }}>⠿</span>
      <span style={{ fontSize: isMobile ? "15px" : "14px", color: appTheme.primaryText }}>{item}</span>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
function SortableStapleRow({ id, item, isChecked, isHidden, isEditing, isMobile, appTheme, ACCENT, onToggle, onHideToggle, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={{ ...style, display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px", borderRadius: "14px", background: isHidden && isEditing ? appTheme.softBackground : isChecked ? appTheme.softBackground : appTheme.cardBackground, border: appTheme.cardBorder, cursor: isEditing ? "default" : "pointer", opacity: isDragging ? 0.35 : isHidden && isEditing ? 0.5 : 1, transition: isDragging ? "none" : "all 0.18s ease" }}
      onClick={() => !isEditing && onToggle(item)}
    >
      {isEditing && (
        <span {...listeners} {...attributes} style={{ color: appTheme.mutedText, cursor: "grab", fontSize: "16px", flexShrink: 0, touchAction: "none", lineHeight: 1 }}>⠿</span>
      )}
      {!isEditing && (
        <div style={{ width: "22px", height: "22px", borderRadius: "50%", border: isChecked ? `2px solid ${ACCENT}` : "2px solid rgba(0,0,0,0.18)", background: isChecked ? ACCENT : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.18s ease" }}>
          {isChecked && <svg viewBox="0 0 12 12" width="11" height="11" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6l3 3 5-5"/></svg>}
        </div>
      )}
      <span style={{ flex: 1, fontSize: isMobile ? "15px" : "14px", color: isChecked && !isEditing ? appTheme.secondaryText : appTheme.primaryText, textDecoration: isChecked && !isEditing ? "line-through" : "none", opacity: isChecked && !isEditing ? 0.6 : 1 }}>{item}</span>
      {isEditing && (
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <button onClick={(e) => { e.stopPropagation(); onHideToggle(item); }} style={{ width: "28px", height: "28px", borderRadius: "999px", border: appTheme.pillBorder, background: isHidden ? appTheme.softBackground : appTheme.pillBackground, color: isHidden ? appTheme.mutedText : appTheme.secondaryText, fontSize: "14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} title={isHidden ? "Show" : "Hide"}>
            {isHidden
              ? <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              : <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            }
          </button>
          <button onClick={(e) => { e.stopPropagation(); onRemove(item); }} style={{ width: "28px", height: "28px", borderRadius: "999px", border: "1px solid rgba(220,38,38,0.25)", background: "rgba(220,38,38,0.06)", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "16px" }}>×</button>
        </div>
      )}
    </div>
  );
}

function ShoppingTab({
  shoppingList,
  checkedItems,
  stapleItems,
  hiddenStaples,
  setHiddenStaples,
  customStaples,
  setCustomStaples,
  setCheckedItems,
  newItem,
  setNewItem,
  addManualItem,
  addNamedManualItem,
  cardStyle,
  appTheme,
  setActiveTab,
  groupedShoppingList,
  isMobile,
  toggleItem,
  manualItems,
  removeManualItem,
  plan,
  pantryItems,
  setPantryItems,
  removedDefaultStaples,
  setRemovedDefaultStaples,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(null);
  const [showNewPlanModal, setShowNewPlanModal] = useState(false);
  const [newStaple, setNewStaple] = useState("");
  const [activeStapleId, setActiveStapleId] = useState(null);
  const [staplesCollapsed, setStaplesCollapsed] = useState(false);
  const [staplesOrder, setStaplesOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem("staplesOrder") || "null"); }
    catch { return null; }
  });
  const [addingToSection, setAddingToSection] = useState(null);
  const [newSectionItem, setNewSectionItem] = useState("");

  // Persist section order (header drag)
  const [sectionOrder, setSectionOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem("shoppingSectionOrder") || "null"); }
    catch { return null; }
  });
  const [draggingSection, setDraggingSection] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null);

  // Persist item → section overrides and item order within sections
  const [itemSectionOverrides, setItemSectionOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem("itemSectionOverrides") || "{}"); }
    catch { return {}; }
  });
  const [sectionItemOrder, setSectionItemOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sectionItemOrder") || "{}"); }
    catch { return {}; }
  });

  // Active drag id for DragOverlay
  const [activeId, setActiveId] = useState(null);

  const pantrySet = useMemo(() => new Set((pantryItems || []).map((i) => i.toLowerCase())), [pantryItems]);
  const ACCENT = appTheme.accentColor;

  // dnd-kit sensors — pointer + touch support
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  // Build display groups: merge groupedShoppingList + overrides + custom order
  const displayGroups = useMemo(() => {
    const itemToSection = {};
    Object.entries(groupedShoppingList).forEach(([section, items]) => {
      items.forEach((item) => { itemToSection[item] = section; });
    });
    // Apply section overrides
    Object.entries(itemSectionOverrides).forEach(([item, section]) => {
      if (item in itemToSection) itemToSection[item] = section;
    });
    // Build groups
    const groups = {};
    Object.entries(itemToSection).forEach(([item, section]) => {
      if (!groups[section]) groups[section] = [];
      groups[section].push(item);
    });
    // Apply custom order within each section
    Object.keys(groups).forEach((section) => {
      const customOrder = sectionItemOrder[section];
      if (customOrder) {
        const ordered = customOrder.filter((i) => groups[section].includes(i));
        const remaining = groups[section].filter((i) => !customOrder.includes(i));
        groups[section] = [...ordered, ...remaining];
      }
    });
    return groups;
  }, [groupedShoppingList, itemSectionOverrides, sectionItemOrder]);

  // Helper: find which section an item currently lives in
  function findSection(id) {
    for (const [section, items] of Object.entries(displayGroups)) {
      if (items.includes(id)) return section;
    }
    return null;
  }

  // Find plan recipes that use a given ingredient
  function findLinkedRecipes(ingredient) {
    const ingLower = ingredient.toLowerCase();
    const linked = new Set();
    Object.values(plan || {}).forEach((dayPlan) => {
      ["breakfast", "lunch", "dinner"].forEach((mt) => {
        const meal = dayPlan?.[mt];
        if (meal?.ingredients?.some((i) => i.toLowerCase().includes(ingLower) || ingLower.includes(i.toLowerCase().replace(/^[\d\s./]+\w*\s*/, "")))) {
          linked.add(meal.name);
        }
      });
    });
    return [...linked];
  }

  function handleDeleteItem(item, isManual) {
    const linked = findLinkedRecipes(item);
    if (linked.length > 0) setDeleteWarning({ item, recipes: linked, isManual });
    else confirmDelete(item, isManual);
  }

  function confirmDelete(item, isManual) {
    if (isManual) removeManualItem(item);
    else setPantryItems((prev) => prev.includes(item.toLowerCase()) ? prev : [...prev, item.toLowerCase()]);
    setDeleteWarning(null);
  }

  const [toast, setToast] = useState(null);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  function handleDeleteStaple(item) {
    const linked = findLinkedRecipes(item);
    if (linked.length > 0) {
      setDeleteWarning({ item, recipes: linked, isStaple: true });
    } else {
      confirmDeleteStaple(item);
    }
  }

  function confirmDeleteStaple(item) {
    const lower = item.toLowerCase();
    // Remove from customStaples if present
    setCustomStaples((prev) => prev.filter((s) => s !== lower));
    // Also suppress from defaultKitchenStaples matching
    setRemovedDefaultStaples((prev) => prev.includes(lower) ? prev : [...prev, lower]);
    setDeleteWarning(null);
  }

  function generateNewWeeklyList() {
    // Clear only checked statuses — preserves staples, section order, aisle layout
    setCheckedItems({});
    setShowNewPlanModal(false);
    showToast("List refreshed for your new week");
  }

  function moveToStaples(item) {
    const lower = item.toLowerCase();
    if (!customStaples.includes(lower)) {
      setCustomStaples((prev) => [...prev, lower]);
      showToast(`"${item}" moved to Kitchen Staples`);
    }
  }

  // ── dnd-kit handlers ──────────────────────────────────────────────────────
  function handleDragStart({ active }) {
    setActiveId(active.id);
    setDraggingSection(null);
    setDragOverSection(null);
  }

  function handleDragEnd({ active, over }) {
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const fromSection = findSection(active.id);
    const toSection = findSection(over.id);

    if (!fromSection) return;

    if (fromSection === toSection) {
      // Same section — reorder
      const items = displayGroups[fromSection];
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const newOrder = arrayMove(items, oldIndex, newIndex);
      const updated = { ...sectionItemOrder, [fromSection]: newOrder };
      setSectionItemOrder(updated);
      localStorage.setItem("sectionItemOrder", JSON.stringify(updated));
    } else if (toSection) {
      // Cross-section move
      const fromItems = displayGroups[fromSection].filter((i) => i !== active.id);
      const toItems = [...(displayGroups[toSection] || [])];
      const overIndex = toItems.indexOf(over.id);
      if (overIndex !== -1) toItems.splice(overIndex, 0, active.id);
      else toItems.push(active.id);

      const newOverrides = { ...itemSectionOverrides, [active.id]: toSection };
      const newOrder = { ...sectionItemOrder, [fromSection]: fromItems, [toSection]: toItems };

      setItemSectionOverrides(newOverrides);
      setSectionItemOrder(newOrder);
      localStorage.setItem("itemSectionOverrides", JSON.stringify(newOverrides));
      localStorage.setItem("sectionItemOrder", JSON.stringify(newOrder));
    }
  }

  // Section-level drag helpers (unchanged from before)
  function reorderSections(from, to) {
    const base = orderedGroups;
    const next = [...base];
    const fi = next.indexOf(from); const ti = next.indexOf(to);
    if (fi === -1 || ti === -1) return;
    next.splice(fi, 1); next.splice(ti, 0, from);
    setSectionOrder(next);
    localStorage.setItem("shoppingSectionOrder", JSON.stringify(next));
  }

  const allGroups = Object.keys(displayGroups);
  const orderedGroups = sectionOrder
    ? [...sectionOrder.filter((g) => allGroups.includes(g)), ...allGroups.filter((g) => !sectionOrder.includes(g))]
    : allGroups;

  function visibleItems(items) {
    if (isEditing) return items;
    return items.filter((item) => !pantrySet.has(item.toLowerCase()));
  }

  const totalRemaining = shoppingList.filter(
    (item) => !checkedItems[item] && !pantrySet.has(item.toLowerCase())
  ).length;

  // All item ids across all sections (for the flat DndContext)
  const allItemIds = orderedGroups.flatMap((g) => displayGroups[g] || []);

  // Ordered staple items with custom sort persisted
  const orderedStaples = useMemo(() => {
    const visible = stapleItems.filter((i) => isEditing || !hiddenStaples.includes(i.toLowerCase()));
    if (!staplesOrder) return visible;
    const ordered = staplesOrder.filter((i) => visible.includes(i));
    const remaining = visible.filter((i) => !staplesOrder.includes(i));
    return [...ordered, ...remaining];
  }, [stapleItems, hiddenStaples, isEditing, staplesOrder]);

  function handleStapleDragStart({ active }) {
    setActiveStapleId(active.id);
  }

  function handleStapleDragEnd({ active, over }) {
    setActiveStapleId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = orderedStaples.indexOf(active.id);
    const newIndex = orderedStaples.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(orderedStaples, oldIndex, newIndex);
    setStaplesOrder(newOrder);
    localStorage.setItem("staplesOrder", JSON.stringify(newOrder));
  }

  return (
    <>
      {/* Header */}
      <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? "24px" : "30px", fontWeight: "600", color: appTheme.primaryText, lineHeight: 1.1, fontFamily: appTheme.fontHeading }}>
            {isEditing ? "Edit Mode" : "Shopping List"}
          </h2>
          {!isEditing && (
            <div style={{ marginTop: "4px", fontSize: "13px", color: appTheme.secondaryText }}>
              {shoppingList.length === 0 ? "Add meals to your plan to generate a list" : `${totalRemaining} items remaining`}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {/* New Plan button */}
          <button
            onClick={() => setShowNewPlanModal(true)}
            title="Start new week"
            style={{ width: "38px", height: "38px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, color: appTheme.secondaryText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
              <line x1="12" y1="14" x2="12" y2="18"/>
              <line x1="10" y1="16" x2="14" y2="16"/>
            </svg>
          </button>

          <button
            onClick={() => { setIsEditing((p) => !p); setAddingToSection(null); setDraggingSection(null); setDragOverSection(null); }}
            style={{ width: "38px", height: "38px", borderRadius: "999px", border: isEditing ? appTheme.accentBorder : appTheme.pillBorder, background: isEditing ? appTheme.accentColor : appTheme.pillBackground, color: isEditing ? "#ffffff" : appTheme.secondaryText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            {isEditing
              ? <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
              : <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            }
          </button>
        </div>
      </div>


      {/* Empty state */}
      {shoppingList.length === 0 && (
        <div style={{ ...cardStyle, textAlign: "center", padding: "40px 20px" }}>
          <div style={{ width: "64px", height: "64px", borderRadius: "999px", background: appTheme.accentLight, border: appTheme.accentBorder, display: "flex", alignItems: "center", justifyContent: "center", color: ACCENT, margin: "0 auto 14px" }}>
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="20" r="1"/><circle cx="17" cy="20" r="1"/><path d="M3 4h2l2.4 10.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 2-1.6L21 7H7"/></svg>
          </div>
          <div style={{ fontSize: "18px", fontWeight: "700", color: appTheme.primaryText, marginBottom: "8px" }}>Your list is empty</div>
          <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.6, maxWidth: "240px", margin: "0 auto" }}>Add meals to your plan and ingredients will appear here automatically.</div>
          <button onClick={() => setActiveTab("plan")} style={{ marginTop: "18px", padding: "12px 22px", borderRadius: "999px", border: appTheme.accentBorder, background: appTheme.accentLight, color: ACCENT, fontWeight: "700", fontSize: "14px", cursor: "pointer" }}>Go to Plan</button>
        </div>
      )}

      {/* Grouped sections wrapped in DndContext */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={allItemIds} strategy={verticalListSortingStrategy}>
          {orderedGroups.map((group) => {
            const rawItems = displayGroups[group] || [];
            const filtered = visibleItems(rawItems.filter((i) => i && i.trim()));
            if (filtered.length === 0 && !isEditing) return null;
            if (rawItems.filter((i) => i && i.trim()).length === 0) return null;

            const checkedInGroup = filtered.filter((i) => checkedItems[i]).length;
            const isDragTarget = dragOverSection === group && draggingSection !== group;

            return (
              <div
                key={group}
                draggable={isEditing && !activeId}
                onDragStart={(e) => { if (activeId) { e.preventDefault(); return; } setDraggingSection(group); }}
                onDragOver={(e) => { e.preventDefault(); setDragOverSection(group); }}
                onDrop={() => { if (draggingSection && draggingSection !== group) reorderSections(draggingSection, group); setDragOverSection(null); }}
                onDragEnd={() => { setDraggingSection(null); setDragOverSection(null); }}
                style={{ ...cardStyle, marginBottom: "12px", padding: isMobile ? "14px" : "16px", opacity: draggingSection === group ? 0.45 : 1, border: isDragTarget ? `2px solid ${ACCENT}` : cardStyle.border, transition: "opacity 0.15s, border 0.15s" }}
              >
                {/* Section header */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: "10px", gap: "6px" }}>
                  {isEditing && <span style={{ color: appTheme.mutedText, cursor: "grab", padding: "0 4px", fontSize: "16px", lineHeight: 1 }}>⠿</span>}
                  <span style={{ color: ACCENT }}>{GROUP_ICONS[group]}</span>
                  <span style={{ fontWeight: "700", fontSize: "15px", color: appTheme.primaryText, flex: 1 }}>{GROUP_LABELS[group]}</span>
                  <span style={{ fontSize: "12px", color: appTheme.secondaryText, fontWeight: "600" }}>{checkedInGroup}/{filtered.length}</span>
                  {isEditing && (
                    <button onClick={() => { setAddingToSection(addingToSection === group ? null : group); setNewSectionItem(""); }} style={{ width: "26px", height: "26px", borderRadius: "999px", border: appTheme.accentBorder, background: appTheme.accentLight, color: ACCENT, fontSize: "18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>+</button>
                  )}
                </div>

                {/* Progress bar */}
                <div style={{ height: "3px", borderRadius: "999px", background: appTheme.softBackground, overflow: "hidden", marginBottom: "10px" }}>
                  <div style={{ width: `${filtered.length > 0 ? (checkedInGroup / filtered.length) * 100 : 0}%`, height: "100%", borderRadius: "999px", background: checkedInGroup === filtered.length ? "#22c55e" : ACCENT, transition: "width 0.3s ease" }} />
                </div>

                {/* Inline section add */}
                {isEditing && addingToSection === group && (
                  <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
                    <input autoFocus value={newSectionItem} onChange={(e) => setNewSectionItem(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && newSectionItem.trim()) { addNamedManualItem(newSectionItem.trim()); setNewSectionItem(""); setAddingToSection(null); } if (e.key === "Escape") setAddingToSection(null); }} placeholder={`Add to ${GROUP_LABELS[group]}...`} style={{ flex: 1, padding: "8px 12px", borderRadius: "999px", border: appTheme.accentBorder, background: appTheme.inputBackground, fontSize: "13px", color: appTheme.primaryText, outline: "none" }} />
                    <button onClick={() => { if (newSectionItem.trim()) { addNamedManualItem(newSectionItem.trim()); setNewSectionItem(""); setAddingToSection(null); } }} style={{ width: "34px", height: "34px", borderRadius: "999px", border: appTheme.accentBorder, background: ACCENT, color: "#fff", fontSize: "18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>+</button>
                  </div>
                )}

                {/* Item rows */}
                <div style={{ display: "grid", gap: "2px" }}>
                  {filtered.map((item) => (
                    <SortableItemRow
                      key={item}
                      id={item}
                      item={item}
                      display={item}
                      isChecked={checkedItems[item] || false}
                      isManual={manualItems.includes(item)}
                      inPantry={customStaples.includes(item.toLowerCase())}
                      isEditing={isEditing}
                      isMobile={isMobile}
                      appTheme={appTheme}
                      ACCENT={ACCENT}

                      onToggle={toggleItem}
                      onDelete={handleDeleteItem}
                      onPantryToggle={moveToStaples}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </SortableContext>

        {/* Ghost card shown while dragging */}
        <DragOverlay modifiers={[restrictToVerticalAxis]}>
          {activeId ? <DragGhostCard item={activeId} appTheme={appTheme} isMobile={isMobile} /> : null}
        </DragOverlay>
      </DndContext>

      {/* Kitchen Staples */}
      <div style={{ ...cardStyle, marginBottom: "12px", padding: isMobile ? "14px" : "16px" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: isEditing || stapleItems.filter((s) => !hiddenStaples.includes(s.toLowerCase())).length > 0 ? "10px" : "0", gap: "6px" }}>
          {isEditing && <span style={{ color: appTheme.mutedText, cursor: "grab", padding: "0 4px", fontSize: "16px", lineHeight: 1 }}>⠿</span>}
          <span style={{ color: ACCENT }}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </span>
          <span style={{ fontWeight: "700", fontSize: "15px", color: appTheme.primaryText, flex: 1 }}>Kitchen Staples</span>
          {isEditing && (
            <button
              onClick={() => setStaplesCollapsed((p) => !p)}
              title={staplesCollapsed ? "Show staples" : "Hide staples"}
              style={{ width: "26px", height: "26px", borderRadius: "999px", border: appTheme.pillBorder, background: staplesCollapsed ? appTheme.softBackground : appTheme.pillBackground, color: staplesCollapsed ? appTheme.mutedText : appTheme.secondaryText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
            >
              {staplesCollapsed
                ? <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          )}
          {isEditing && (
            <button
              onClick={() => { setAddingToSection(addingToSection === "staples" ? null : "staples"); setNewStaple(""); }}
              style={{ width: "26px", height: "26px", borderRadius: "999px", border: appTheme.accentBorder, background: appTheme.accentLight, color: ACCENT, fontSize: "18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
            >+</button>
          )}
          {(() => {
            const visibleStaples = stapleItems.filter((s) => !hiddenStaples.includes(s.toLowerCase()));
            const checkedInStaples = visibleStaples.filter((s) => checkedItems[s]).length;
            return (
              <span style={{ fontSize: "12px", color: appTheme.secondaryText, fontWeight: "600" }}>
                {checkedInStaples}/{visibleStaples.length}
              </span>
            );
          })()}
        </div>

        {(() => {
          const visibleStaples = stapleItems.filter((s) => !hiddenStaples.includes(s.toLowerCase()));
          const checkedInStaples = visibleStaples.filter((s) => checkedItems[s]).length;
          return (
            <div style={{ height: "3px", borderRadius: "999px", background: appTheme.softBackground, overflow: "hidden", marginBottom: "10px" }}>
              <div style={{ width: `${visibleStaples.length > 0 ? (checkedInStaples / visibleStaples.length) * 100 : 0}%`, height: "100%", borderRadius: "999px", background: checkedInStaples === visibleStaples.length && visibleStaples.length > 0 ? "#22c55e" : ACCENT, transition: "width 0.3s ease" }} />
            </div>
          );
        })()}

        {isEditing && addingToSection === "staples" && (
          <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
            <input autoFocus value={newStaple} onChange={(e) => setNewStaple(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && newStaple.trim()) { setCustomStaples((prev) => [...prev, newStaple.trim().toLowerCase()]); setNewStaple(""); setAddingToSection(null); } if (e.key === "Escape") setAddingToSection(null); }} placeholder="Add a staple..." style={{ flex: 1, padding: "8px 12px", borderRadius: "999px", border: appTheme.accentBorder, background: appTheme.inputBackground, fontSize: "13px", color: appTheme.primaryText, outline: "none" }} />
            <button onClick={() => { if (!newStaple.trim()) return; setCustomStaples((prev) => [...prev, newStaple.trim().toLowerCase()]); setNewStaple(""); setAddingToSection(null); }} style={{ width: "34px", height: "34px", borderRadius: "999px", border: appTheme.accentBorder, background: ACCENT, color: "#fff", fontSize: "18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>+</button>
          </div>
        )}

        {stapleItems.length === 0 && !isEditing ? (
          <div style={{ fontSize: "13px", color: appTheme.mutedText, fontStyle: "italic" }}>Staple ingredients from your plan will appear here.</div>
        ) : staplesCollapsed && !isEditing ? (
          <div style={{ fontSize: "13px", color: appTheme.mutedText, fontStyle: "italic" }}>All staples stocked — nothing needed.</div>
        ) : staplesCollapsed ? null : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragStart={handleStapleDragStart}
            onDragEnd={handleStapleDragEnd}
          >
            <SortableContext items={orderedStaples} strategy={verticalListSortingStrategy}>
              <div style={{ display: "grid", gap: "2px" }}>
                {orderedStaples.map((item) => {
                  const isChecked = checkedItems[item] || false;
                  const isHidden = hiddenStaples.includes(item.toLowerCase());
                  return (
                    <SortableStapleRow
                      key={item}
                      id={item}
                      item={item}
                      isChecked={isChecked}
                      isHidden={isHidden}
                      isEditing={isEditing}
                      isMobile={isMobile}
                      appTheme={appTheme}
                      ACCENT={ACCENT}
                      onToggle={toggleItem}
                      onHideToggle={(i) => { const l = i.toLowerCase(); setHiddenStaples((prev) => prev.includes(l) ? prev.filter((s) => s !== l) : [...prev, l]); }}
                      onRemove={handleDeleteStaple}
                    />
                  );
                })}
              </div>
            </SortableContext>
            <DragOverlay modifiers={[restrictToVerticalAxis]}>
              {activeStapleId ? <DragGhostCard item={activeStapleId} appTheme={appTheme} isMobile={isMobile} /> : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Delete dependency warning modal */}
      {/* New Plan confirmation modal */}
      {showNewPlanModal && (
        <div onClick={() => setShowNewPlanModal(false)} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: appTheme.cardBackground, borderRadius: "24px", padding: "28px 24px", maxWidth: "340px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            {/* Calendar icon */}
            <div style={{ width: "52px", height: "52px", borderRadius: "16px", background: appTheme.accentLight, border: appTheme.accentBorder, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "18px" }}>
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
                <line x1="12" y1="14" x2="12" y2="18"/>
                <line x1="10" y1="16" x2="14" y2="16"/>
              </svg>
            </div>
            <div style={{ fontSize: "20px", fontWeight: "500", color: appTheme.primaryText, marginBottom: "10px", fontFamily: appTheme.fontHeading }}>
              Ready for next week?
            </div>
            <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.65, marginBottom: "24px" }}>
              This will clear your current checked items. Your Kitchen Staples and Aisle Order will stay saved. We'll pull in ingredients for your new recipes.
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowNewPlanModal(false)}
                style={{ flex: 1, padding: "12px", borderRadius: "14px", border: appTheme.pillBorder, background: appTheme.pillBackground, color: appTheme.secondaryText, fontWeight: "600", fontSize: "14px", cursor: "pointer" }}
              >Cancel</button>
              <button
                onClick={generateNewWeeklyList}
                style={{ flex: 1, padding: "12px", borderRadius: "14px", border: "none", background: ACCENT, color: "#ffffff", fontWeight: "700", fontSize: "14px", cursor: "pointer" }}
              >Generate My List</button>
            </div>
          </div>
        </div>
      )}

      {deleteWarning && (
        <div onClick={() => setDeleteWarning(null)} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: appTheme.cardBackground, borderRadius: "20px", padding: "24px", maxWidth: "340px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: "16px", fontWeight: "700", color: appTheme.primaryText, marginBottom: "8px" }}>Wait!</div>
            <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.6, marginBottom: "20px" }}>
              You need <strong style={{ color: appTheme.primaryText }}>{deleteWarning.item}</strong> for{" "}
              {deleteWarning.recipes.map((r, i) => (<span key={r}><strong style={{ color: appTheme.primaryText }}>{r}</strong>{i < deleteWarning.recipes.length - 1 ? " and " : ""}</span>))}. Delete anyway?
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => setDeleteWarning(null)} style={{ flex: 1, padding: "11px", borderRadius: "12px", border: appTheme.pillBorder, background: appTheme.pillBackground, color: appTheme.secondaryText, fontWeight: "600", fontSize: "14px", cursor: "pointer" }}>Keep</button>
              <button onClick={() => deleteWarning.isStaple ? confirmDeleteStaple(deleteWarning.item) : confirmDelete(deleteWarning.item, deleteWarning.isManual)} style={{ flex: 1, padding: "11px", borderRadius: "12px", border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.08)", color: "#dc2626", fontWeight: "700", fontSize: "14px", cursor: "pointer" }}>Remove</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ height: isMobile ? "100px" : "40px" }} />

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: "fixed", bottom: isMobile ? "90px" : "28px", left: "50%",
          transform: "translateX(-50%)",
          background: appTheme.accentColor, color: "#ffffff",
          padding: "10px 20px", borderRadius: "999px",
          fontSize: "13px", fontWeight: "600",
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          zIndex: 3000, whiteSpace: "nowrap",
          animation: "slideFadeInForward 0.22s cubic-bezier(0.22, 1, 0.36, 1)",
          pointerEvents: "none",
        }}>
          {toast}
        </div>
      )}
    </>
  );
}

export default ShoppingTab;
